import fetchWithAuth from '../components/fetchWithAuth'; // ✅ SECURITY: Use CSRF-protected fetch
import config from '../config';
import { cachedRequest } from '../utils/requestCache';

const API_URL = `${config.API_BASE_URL}/api/company`;

// GET compartido (TASK-122): una sola petición en vuelo por URL y caché breve en memoria.
// El signal solo cancela la espera de este consumidor, no la petición compartida.
// force ignora la caché (ej. refresco periódico del DashboardContext).
const getJsonShared = (url, { signal, force } = {}) => cachedRequest(url, async () => {
    const response = await fetchWithAuth(url);
    if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
    return response.json();
}, { signal, force });

const companyService = {
    // Obtener datos de la empresa. signal permite cancelar la espera con AbortController
    getCompany: (signal, { force = false } = {}) => getJsonShared(API_URL, { signal, force }),

    // Uso mensual de turnos del plan (Sidebar y MonthlyUsageBar lo comparten)
    getTurnUsage: (signal) => getJsonShared(`${API_URL}/turn-usage`, { signal }),

    updateCompany: async (formData) => {
        const isFormData = formData instanceof FormData;
        const response = await fetchWithAuth(API_URL, {
            method: 'PUT',
            body: isFormData ? formData : JSON.stringify(formData),
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    updateCurrentCompany: async (formData) => {
        const isFormData = formData instanceof FormData;
        const response = await fetchWithAuth(API_URL, {
            method: 'PUT',
            body: isFormData ? formData : JSON.stringify(formData),
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Obtener horarios. signal permite cancelar la request
    getSchedule: (branchId, signal) => {
        const url = branchId ? `${API_URL}/schedule?branchId=${branchId}` : `${API_URL}/schedule`;
        return getJsonShared(url, { signal });
    },

    // Guardar horarios de la empresa (reemplaza los existentes para los días enviados)
    addSchedule: async (scheduleData) => {
        const response = await fetchWithAuth(`${API_URL}/schedule`, {
            method: 'POST',
            body: JSON.stringify({ hours: scheduleData }),
        });
        // FIX: Proteger ante respuestas no-JSON (ej: 502 Bad Gateway devuelve HTML)
        // Esto causaba "Unexpected token - in JSON at position 0"
        if (!response.ok) {
            let errMsg = `Error ${response.status} al guardar horario`;
            try {
                const errData = await response.json();
                errMsg = errData.message || errMsg;
            } catch { /* la respuesta no era JSON (ej: HTML de error del servidor) */ }
            throw new Error(errMsg);
        }
        return response.json();
    },

    deleteSchedule: async (id) => {
        const response = await fetchWithAuth(`${API_URL}/schedule/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // --- Feriados / Excepciones ---
    // Obtener feriados. signal permite cancelar la request
    getHolidays: (companyId, signal) => getJsonShared(`${API_URL}/dates`, { signal }),

    addHoliday: async (date, description, type = 'closed') => {
        const response = await fetchWithAuth(`${API_URL}/dates`, {
            method: 'POST',
            body: JSON.stringify({ date, description, type })
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    deleteHoliday: async (dateId) => {
        const response = await fetchWithAuth(`${API_URL}/dates/${dateId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Métodos del Dashboard. signal permite cancelar la request
    getBranches: (signal, { force = false } = {}) => getJsonShared(`${config.API_BASE_URL}/api/branches`, { signal, force }),

    searchClients: async (query) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/company/clients?query=${query}`);
        if (!response.ok) throw new Error('Error buscando clientes');
        return response.json();
    },

    // Obtener turnos próximos. signal permite cancelar la request
    getUpcomingTurns: async (companyId, branchId = null, signal) => {
        let url = `${config.API_BASE_URL}/api/turn/upcoming-turns/${companyId}`;
        if (branchId) {
            url += `?branchId=${branchId}`;
        }
        const response = await fetchWithAuth(url, { signal });
        if (!response.ok) {
            if (response.status === 429) {
                console.warn('Rate limit (429) al obtener turnos próximos. Retornando lista vacía.');
            }
            return [];
        }
        const data = await response.json().catch(() => ([]));
        return Array.isArray(data) ? data : [];
    },

    confirmTurn: async (turnId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/confirm/${turnId}`, {
            method: 'PUT',
            body: JSON.stringify({ confirmed: true })
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    rescheduleTurn: async (turnId, newDate, newTime) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/reschedule/${turnId}`, {
            method: 'PUT',
            body: JSON.stringify({ newDate, newTime })
        });
        if (!response.ok) {
            // FIX: Proteger ante respuestas no-JSON antes de lanzar el error
            let errMsg = `Error ${response.status} al reprogramar turno`;
            try {
                const errorData = await response.json();
                errMsg = errorData.message || errMsg;
            } catch { /* respuesta no era JSON */ }
            throw new Error(errMsg);
        }
        return response.json();
    },

    cancelTurn: async (turnId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/cancel/${turnId}`, {
            method: 'PUT'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    completeTurn: async (turnId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/complete/${turnId}`, {
            method: 'PUT'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    revertTurn: async (turnId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/revert/${turnId}`, {
            method: 'PUT'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    getTurnsToReview: async (companyId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/to-review/${companyId}`);
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    markAsAbsent: async (turnId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/mark-absent/${turnId}`, {
            method: 'PUT'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    createSubscription: async (companyId, email, planId = 'profesional', billingCycle = 'monthly', customAddons = []) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/payment/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyId, email, planId, billingCycle, customAddons })
        });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.message || 'Error al crear suscripción');
        }
        return response.json();
    },

    getAddonsCatalog: async () => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/company/addons`);
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    getSubscriptionEntitlements: async () => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/company/subscription`);
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    linkSubscription: async (companyId, preapprovalId, planId = 'profesional', billingCycle = 'monthly') => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/payment/link-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyId, preapprovalId, planId, billingCycle })
        });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.message || 'Error al vincular suscripción');
        }
        return response.json();
    },

    // --- Clientes (CRM) ---
    // Obtener lista paginada de clientes con KPIs
    getClients: async (params, signal) => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_URL}/clients-list?${queryParams}`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Obtener historial completo de un cliente (turnos + stats)
    getClientHistory: async (userId, signal) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/history`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Obtener la ficha del cliente (datos del rubro: patente, mascota, etc.)
    getClientProfile: async (userId, signal) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/profile`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Crear o actualizar la ficha del cliente (UPSERT)
    updateClientProfile: async (userId, profileData, notes) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/profile`, {
            method: 'PUT',
            body: JSON.stringify({ profileData, notes })
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Actualizar notas de un turno específico (qué se hizo en la visita)
    updateTurnNotes: async (turnId, notes, entityId = null) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/turn/notes/${turnId}`, {
            method: 'PUT',
            body: JSON.stringify({ notes, entityId })
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // ─── 🩺 MÓDULO SANITARIO: Consultas Inmutables y Recetas Médicas ───
    // Crear una consulta médica inmutable con firma SHA-256 (Ley 26.529)
    createMedicalConsultation: async (userId, data) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/consultations`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Obtener historial de consultas médicas del paciente
    getMedicalConsultations: async (userId, signal) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/consultations`, { signal });
        if (!response.ok) {
            // Se conserva el status: 403 = acceso reservado a profesionales (Ley 26.529), no un fallo de red
            const body = await response.json().catch(() => ({}));
            const error = new Error(body.message || `Error ${response.status}: ${response.statusText}`);
            error.status = response.status;
            error.code = body.code;
            throw error;
        }
        return response.json();
    },

    // Emitir receta médica digital con código QR de verificación (Ley 27.553)
    createMedicalPrescription: async (userId, data) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/prescriptions`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // Obtener recetas médicas emitidas para el paciente
    getMedicalPrescriptions: async (userId, signal) => {
        const response = await fetchWithAuth(`${API_URL}/clients/${userId}/prescriptions`, { signal });
        if (!response.ok) {
            // Se conserva el status: 403 = acceso reservado a profesionales (Ley 26.529), no un fallo de red
            const body = await response.json().catch(() => ({}));
            const error = new Error(body.message || `Error ${response.status}: ${response.statusText}`);
            error.status = response.status;
            error.code = body.code;
            throw error;
        }
        return response.json();
    },

    // Verificación pública de receta para farmacias (sin auth) conforme a Ley 27.553
    verifyPrescription: async (prescriptionCode) => {
        const response = await fetch(`${API_URL}/prescriptions/verify/${encodeURIComponent(prescriptionCode)}`);
        if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.message || `Error ${response.status}: Receta médica no encontrada`);
        }
        return response.json();
    },

    // --- Ausencias y Vacaciones de Empleados (Módulo 2) ---
    getEmployeeUnavailability: async (employeeId, signal) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/employee/${employeeId}/unavailability`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    addEmployeeUnavailability: async (employeeId, startDateTime, endDateTime) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/employee/${employeeId}/unavailability`, {
            method: 'POST',
            body: JSON.stringify({ start_date_time: startDateTime, end_date_time: endDateTime })
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    deleteEmployeeUnavailability: async (unavailabilityId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/employee/unavailability/${unavailabilityId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    approveEmployeeUnavailability: async (unavailabilityId) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/employee/unavailability/${unavailabilityId}/approve`, {
            method: 'PUT'
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    rejectEmployeeUnavailability: async (unavailabilityId, reason) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/employee/unavailability/${unavailabilityId}/reject`, {
            method: 'PUT',
            body: JSON.stringify({ reason })
        });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // --- M5: Bloqueos masivos y recurrentes (multi-empleado) ---
    getEmployees: async (signal) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/employee`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        const data = await response.json();
        return data.data?.employees || data.data || data || [];
    },

    addCompanyUnavailability: async (payload) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/company/unavailability`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `Error ${response.status}: ${response.statusText}`);
        }
        return response.json();
    },

    getCompanyUnavailability: async (signal) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/company/unavailability`, { signal });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        return data.blocks || [];
    },

    deleteCompanyUnavailability: async (id) => {
        const response = await fetchWithAuth(`${config.API_BASE_URL}/api/company/unavailability/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `Error ${response.status}: ${response.statusText}`);
        }
        return response.json();
    },

    // --- Registros de Auditoría y Seguridad (Módulo 6) ---
    getAuditLogs: async (params = {}, signal) => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_URL}/audit-logs?${queryParams}`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    // --- Obras Sociales y Coberturas Médicas (uTurno Health) ---
    getAllHealthInsurances: async (signal) => {
        const response = await fetch(`${config.API_BASE_URL}/api/company/health-insurances`, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    getAcceptedHealthInsurances: async (companyId, signal) => {
        const url = companyId 
            ? `${config.API_BASE_URL}/api/company/${companyId}/health-insurances`
            : `${API_URL}/accepted-health-insurances`;
        const response = companyId 
            ? await fetch(url, { signal })
            : await fetchWithAuth(url, { signal });
        if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
        return response.json();
    },

    updateAcceptedHealthInsurances: async (insurances) => {
        const response = await fetchWithAuth(`${API_URL}/accepted-health-insurances`, {
            method: 'PUT',
            body: JSON.stringify({ insurances })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || `Error ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }
};

export default companyService;
